<template>
  <div
    :class="[
      'min-h-screen flex flex-col transition-colors duration-500',
      darkMode
        ? 'bg-gradient-to-br from-gray-950 to-gray-900 text-white'
        : 'bg-gradient-to-br from-slate-50 to-slate-100 text-gray-900',
    ]"
  >
    <!-- NAVBAR -->
    <nav
      :class="['sticky top-0 z-50 transition-all duration-300', isScrolled ? 'py-2' : 'py-4']"
      :style="{
        background: darkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      }"
    >
      <div class="container mx-auto px-6 lg:px-12">
        <div class="flex justify-between items-center">
          <!-- LOGO -->
          <router-link to="/" class="group relative flex items-center gap-3 transition-all duration-300">
            <div class="relative">
              <div
                v-if="app.school?.logo"
                class="w-11 h-11 rounded-xl overflow-hidden shadow-lg transition-all duration-300 group-hover:shadow-xl"
                :style="{ boxShadow: `0 0 20px -5px var(--primary-color)`, borderRadius: '12px' }"
              >
                <img :src="app.school.logo" alt="Logo" class="w-full h-full object-cover" />
              </div>
              <div
                v-else
                class="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                :style="{ background: `linear-gradient(135deg, var(--primary-color), var(--accent-color))`, boxShadow: `0 0 20px -5px var(--primary-color)` }"
              >
                <font-awesome-icon :icon="['fas', 'graduation-cap']" class="text-lg text-white" />
              </div>
              <div
                class="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                :style="{ border: `2px solid var(--primary-color)`, animation: 'pulse-ring 2s infinite' }"
              ></div>
            </div>
            <div class="hidden sm:flex flex-col">
              <h1 class="text-sm lg:text-lg font-black tracking-tight leading-tight group-hover:scale-105 transition-transform origin-left">
                {{ app.school?.name || 'Școala' }}
              </h1>
              <p class="text-[10px] lg:text-xs opacity-60 font-semibold tracking-widest uppercase">Educație de Calitate</p>
            </div>
          </router-link>

          <!-- DESKTOP NAV -->
          <div class="hidden lg:flex items-center gap-1">
            <router-link
              v-for="nav in navLinks"
              :key="nav.path"
              :to="nav.path"
              active-class=""
              exact-active-class="nav-link--active"
              class="nav-link group relative px-5 py-2 text-sm font-semibold transition-all duration-300 overflow-hidden rounded-lg"
              :class="[darkMode ? 'nav-link--dark' : 'nav-link--light']"
            >
              <div
                class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
                :style="{ background: `linear-gradient(135deg, var(--primary-color), var(--accent-color))`, opacity: '0.08' }"
              ></div>
              <span class="relative flex items-center gap-1.5">
                <font-awesome-icon v-if="nav.icon" :icon="nav.icon" class="text-xs opacity-60 group-hover:opacity-100 transition-all" />
                {{ nav.label }}
              </span>
              <!-- Active / hover underline -->
              <div
                class="nav-underline absolute bottom-0 left-0 h-0.5 transition-all duration-300"
                :style="{ background: `linear-gradient(90deg, var(--primary-color), var(--accent-color))` }"
              ></div>
            </router-link>
          </div>

          <!-- RIGHT CONTROLS -->
          <div class="flex items-center gap-3">
            <button
              @click="toggleDarkMode"
              class="relative p-2.5 rounded-lg transition-all duration-300 group"
              :class="[darkMode ? 'bg-yellow-500/10 hover:bg-yellow-500/20' : 'bg-slate-900/10 hover:bg-slate-900/20']"
            >
              <transition name="rotate" mode="out-in">
                <font-awesome-icon v-if="darkMode" key="sun" icon="fa-solid fa-sun" class="text-lg transition-transform group-hover:rotate-180" :style="{ color: 'var(--accent-color)' }" />
                <font-awesome-icon v-else key="moon" icon="fa-solid fa-moon" class="text-lg transition-transform group-hover:-rotate-180" :style="{ color: 'var(--primary-color)' }" />
              </transition>
            </button>
            <button
              @click="toggleMenu"
              class="lg:hidden relative p-2.5 rounded-lg transition-all duration-300"
              :class="[darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-900/10 hover:bg-slate-900/20']"
            >
              <transition name="flip" mode="out-in">
                <font-awesome-icon v-if="!menuOpen" key="menu" :icon="['fas', 'bars']" class="text-lg" />
                <font-awesome-icon v-else key="close" :icon="['fas', 'xmark']" class="text-lg" />
              </transition>
            </button>
          </div>
        </div>

        <!-- MOBILE NAV -->
        <transition name="slide-down">
          <div
            v-if="menuOpen"
            class="lg:hidden mt-4 pt-4 border-t flex flex-col gap-1"
            :class="darkMode ? 'border-white/10' : 'border-slate-900/10'"
          >
            <router-link
              v-for="nav in navLinks"
              :key="nav.path"
              :to="nav.path"
              active-class=""
              exact-active-class="mobile-nav-link--active"
              class="mobile-nav-link group relative px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-300"
              :class="[darkMode ? 'mobile-nav-link--dark' : 'mobile-nav-link--light']"
              @click="menuOpen = false"
            >
              <span class="flex items-center gap-2">
                <font-awesome-icon v-if="nav.icon" :icon="nav.icon" class="text-xs" />
                {{ nav.label }}
              </span>
              <!-- Active indicator bar -->
              <div class="mobile-nav-active-bar"></div>
            </router-link>
          </div>
        </transition>
      </div>
    </nav>

    <!-- MAIN CONTENT -->
    <main class="flex-grow">
      <router-view />
    </main>

    <!-- FOOTER -->
    <footer class="footer relative mt-auto overflow-hidden" :class="darkMode ? 'footer--dark' : 'footer--light'">
      <div class="footer-accent-line"></div>
      <div class="container mx-auto px-6 lg:px-12 pt-16 pb-10 relative z-10">
        <div class="footer-grid">

          <!-- COL 1: Brand + contact -->
          <div class="footer-brand">
            <div class="footer-logo-row">
              <div class="footer-logo-icon" :style="{ background: `linear-gradient(135deg, var(--primary-color), var(--accent-color))` }">
                <font-awesome-icon :icon="['fas', 'graduation-cap']" class="text-white text-base" />
              </div>
              <div>
                <p class="footer-school-name">{{ app.school?.name || 'Școala Gimnazială' }}</p>
                <p class="footer-school-sub">Educație · Excelență · Comunitate</p>
              </div>
            </div>
            <p class="footer-desc">
              {{ app.school?.description || 'O comunitate educațională modernă, dedicată performanței și dezvoltării armonioase a fiecărui elev.' }}
            </p>
            <ul class="footer-contact-list">
              <li v-if="app.school?.address" class="footer-contact-item">
                <span class="footer-contact-icon"><font-awesome-icon :icon="['fas', 'location-dot']" /></span>
                <span>{{ app.school.address }}, {{ app.school.city }}</span>
              </li>
              <li v-if="app.school?.phone">
                <a :href="`tel:${app.school.phone}`" class="footer-contact-item footer-contact-link">
                  <span class="footer-contact-icon"><font-awesome-icon :icon="['fas', 'phone']" /></span>
                  <span>{{ app.school.phone }}</span>
                </a>
              </li>
              <li v-if="app.school?.email">
                <a :href="`mailto:${app.school.email}`" class="footer-contact-item footer-contact-link">
                  <span class="footer-contact-icon"><font-awesome-icon :icon="['fas', 'envelope']" /></span>
                  <span>{{ app.school.email }}</span>
                </a>
              </li>
            </ul>
          </div>

          <!-- COL 2: Useful links -->
          <div class="footer-col">
            <p class="footer-col-heading">Linkuri utile</p>
            <ul class="footer-links-list">
              <li v-for="l in usefulLinks" :key="l.label">
                <a :href="l.url" target="_blank" class="footer-link">
                  <svg class="footer-link-arrow" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ l.label }}
                </a>
              </li>
              <li v-for="route in privacyLinks" :key="route.to">
                <router-link :to="route.to" active-class="" exact-active-class="footer-router-link--active" class="footer-router-link" :class="darkMode ? 'footer-router-link--dark' : 'footer-router-link--light'">
                  <svg class="footer-link-arrow" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ route.label }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- COL 3: Social + admin -->
          <div class="footer-col footer-col--right">
            <p class="footer-col-heading">Urmărește-ne</p>
            <div class="footer-socials">
              <a
                v-for="social in activeSocials"
                :key="social.id"
                :href="app.school[`${social.id}_url`]"
                target="_blank"
                :title="social.id"
                class="footer-social-btn"
              >
                <font-awesome-icon :icon="social.icon" />
              </a>
            </div>
            <div class="footer-divider"></div>
            <router-link to="/admin/login" class="footer-admin-btn">
              <font-awesome-icon :icon="['fas', 'user-shield']" class="text-sm" />
              Login Admin
            </router-link>
          </div>
        </div>

        <div class="footer-bottom">
          <p class="footer-copy">© {{ new Date().getFullYear() }} {{ app.school?.name }}</p>
          <p class="footer-credit">
            Arhitectură digitală de
            <a href="https://rusin.ro" target="_blank" class="footer-credit-link">Luca Rusin</a>
          </p>
        </div>
      </div>
    </footer>

    <!-- COOKIE BANNER -->
    <transition name="slide-up">
      <div v-if="!cookiesAccepted" class="cookie-banner" :class="darkMode ? 'cookie-banner--dark' : 'cookie-banner--light'">
        <div class="cookie-inner">
          <div class="cookie-icon-wrap">
            <font-awesome-icon :icon="['fas', 'cookie-bite']" class="text-lg" />
          </div>
          <div class="cookie-text">
            <p class="cookie-title">Folosim cookie-uri</p>
            <p class="cookie-desc">
              Pentru cea mai bună experiență pe site-ul nostru.
              <router-link to="/politica-confidentialitate" class="cookie-policy-link">Află mai mult</router-link>
            </p>
          </div>
          <button @click="acceptCookies" class="cookie-accept-btn">Accept</button>
        </div>
      </div>
    </transition>

    <!-- BACK TO TOP -->
    <transition name="fade-scale">
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-8 right-8 z-[60] w-14 h-14 flex items-center justify-center rounded-2xl shadow-2xl transition-all duration-300 group hover:-translate-y-2 active:scale-90"
        :style="{ backgroundColor: 'var(--primary-color)', boxShadow: `0 12px 24px -8px var(--primary-color)` }"
        title="Înapoi sus"
      >
        <div class="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
        <font-awesome-icon :icon="['fas', 'chevron-up']" class="text-xl text-white transition-transform group-hover:scale-125" />
      </button>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAppStore } from '@/stores/useAppStore'

const app = useAppStore()
const darkMode = ref(app.darkMode)
const menuOpen = ref(false)
const showBackToTop = ref(false)
const cookiesAccepted = ref(false)
const isScrolled = ref(false)

const toggleDarkMode = () => {
  app.toggleDark()
  darkMode.value = app.darkMode
  document.documentElement.classList.toggle('dark', darkMode.value)
}
const toggleMenu = () => (menuOpen.value = !menuOpen.value)
const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300
  isScrolled.value = window.scrollY > 50
}
const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

const navLinks = [
  { path: '/',        label: 'Acasă',    icon: ['fas', 'house'],     exact: true },
  { path: '/despre',  label: 'Despre',   icon: ['fas', 'info'],      exact: false },
  { path: '/anunturi',label: 'Anunțuri', icon: ['fas', 'bell'],      exact: false },
  { path: '/contact', label: 'Contact',  icon: ['fas', 'envelope'],  exact: false },
]

const privacyLinks = [
  { to: '/politica-confidentialitate', label: 'Confidențialitate' },
  { to: '/termeni-si-conditii',        label: 'Termeni și Condiții' },
]

const usefulLinks = computed(() => {
  const links = [
    { label: 'Orar',               url: app.school?.orar_url },
    { label: 'Înscrieri',          url: app.school?.inscrieri_url },
    { label: 'Carnet elev',        url: app.school?.carnet_elev_url },
    { label: 'Catalog profesori',  url: app.school?.catalog_profesori_url },
    { label: 'E-learning',         url: app.school?.e_learning_url },
    { label: 'Bibliotecă',         url: app.school?.library_url },
  ]

  // Generare automată link Webmail dacă există carnet_elev_url
  if (app.school?.carnet_elev_url) {
    try {
      const carnetUrl = new URL(app.school.carnet_elev_url)
      const hostnameParts = carnetUrl.hostname.split('.')
      
      // Extragem domeniul principal. Dacă e "catalog.domeniu.ro", luăm doar "domeniu.ro"
      const rootDomain = hostnameParts.length > 2 
        ? hostnameParts.slice(-2).join('.') 
        : carnetUrl.hostname

      links.push({
        label: 'Webmail',
        url: `${carnetUrl.protocol}//mail.${rootDomain}`
      })
    } catch (error) {
      console.warn('URL invalid pentru carnet_elev_url, nu s-a putut genera link-ul de Webmail.')
    }
  }

  return links.filter(l => !!l.url)
})

const activeSocials = computed(() => {
  const platforms = [
    { id: 'facebook',  icon: ['fab', 'facebook'] },
    { id: 'instagram', icon: ['fab', 'instagram'] },
    { id: 'youtube',   icon: ['fab', 'youtube'] },
    { id: 'tiktok',    icon: ['fab', 'tiktok'] },
  ]
  return platforms.filter(p => app.school?.[`${p.id}_url`])
})

const checkCookies = () => {
  cookiesAccepted.value = localStorage.getItem('cookies_accepted') === 'true'
}
const acceptCookies = () => {
  localStorage.setItem('cookies_accepted', 'true')
  cookiesAccepted.value = true
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  document.documentElement.classList.toggle('dark', app.darkMode)
  checkCookies()
})
onBeforeUnmount(() => window.removeEventListener('scroll', handleScroll))
</script>

<style scoped>
/* ─── NAVBAR ANIMATIONS ─────────────────────────────────── */
@keyframes pulse-ring {
  0%, 100% { box-shadow: 0 0 0 0 var(--primary-color); }
  50%       { box-shadow: 0 0 0 6px rgba(0,0,0,0); }
}
.rotate-enter-active, .rotate-leave-active { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); }
.rotate-enter-from  { transform: rotateZ(-180deg); }
.rotate-leave-to    { transform: rotateZ(180deg); }
.flip-enter-active, .flip-leave-active { transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }
.flip-enter-from  { transform: rotateY(-90deg); }
.flip-leave-to    { transform: rotateY(90deg); }
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1); }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-15px); }
.fade-scale-enter-active, .fade-scale-leave-active { transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1); }
.fade-scale-enter-from, .fade-scale-leave-to { opacity: 0; transform: scale(0) rotate(-45deg); }

/* ─── DESKTOP NAV LINKS ──────────────────────────────────── */
.nav-link { border-radius: 8px; }

/* Underline default = 0 width */
.nav-underline { width: 0; }

/* Hover = full width */
.nav-link:hover .nav-underline { width: 100%; }

/* Active state (custom --active class via exactActiveClass) */
.nav-link.nav-link--active {
  color: var(--primary-color);
}
.nav-link--light.nav-link--active {
  background: color-mix(in srgb, var(--primary-color) 8%, transparent);
}
.nav-link--dark.nav-link--active {
  background: color-mix(in srgb, var(--primary-color) 12%, transparent);
}
.nav-link.nav-link--active .nav-underline {
  width: 100%;
}
.nav-link.nav-link--active svg { opacity: 1; }

/* ─── MOBILE NAV LINKS ───────────────────────────────────── */
.mobile-nav-link { position: relative; }
.mobile-nav-active-bar {
  position: absolute;
  left: 0; top: 50%; transform: translateY(-50%);
  width: 3px; height: 0;
  border-radius: 999px;
  background: linear-gradient(180deg, var(--primary-color), var(--accent-color));
  transition: height 0.25s ease;
}

.mobile-nav-link--light:hover { background: color-mix(in srgb, var(--primary-color) 6%, transparent); }
.mobile-nav-link--dark:hover  { background: color-mix(in srgb, var(--primary-color) 10%, transparent); }

/* Active mobile */
.mobile-nav-link.mobile-nav-link--active {
  color: var(--primary-color);
}
.mobile-nav-link--light.mobile-nav-link--active {
  background: color-mix(in srgb, var(--primary-color) 8%, transparent);
}
.mobile-nav-link--dark.mobile-nav-link--active {
  background: color-mix(in srgb, var(--primary-color) 12%, transparent);
}
.mobile-nav-link.mobile-nav-link--active .mobile-nav-active-bar {
  height: 60%;
}

/* ─── FOOTER ─────────────────────────────────────────────── */
.footer { border-top: 1px solid transparent; transition: background 0.5s, color 0.5s; }
.footer--light { background: #ffffff; border-color: #e8eaf0; color: #374151; }
.footer--dark  { background: #0a0f1a; border-color: #1e2a3a; color: #94a3b8; }
.footer-accent-line { height: 3px; background: linear-gradient(90deg, var(--primary-color), #6366f1, var(--accent-color)); }
.footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 48px; padding-bottom: 48px; border-bottom: 1px solid; }
.footer--light .footer-grid { border-color: #e8eaf0; }
.footer--dark  .footer-grid { border-color: #1e2a3a; }
@media (max-width: 1024px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 640px)  { .footer-grid { grid-template-columns: 1fr; gap: 32px; } }

.footer-logo-row { display: flex; align-items: center; gap: 14px; margin-bottom: 20px; }
.footer-logo-icon { width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 14px -4px var(--primary-color); }
.footer-school-name { font-size: 1rem; font-weight: 800; letter-spacing: -0.01em; line-height: 1.2; }
.footer--light .footer-school-name { color: #0f172a; }
.footer--dark  .footer-school-name { color: #f1f5f9; }
.footer-school-sub { font-size: 0.68rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.5; margin-top: 2px; }
.footer-desc { font-size: 0.875rem; line-height: 1.7; opacity: 0.7; max-width: 340px; margin-bottom: 24px; font-style: italic; }
.footer-contact-list { display: flex; flex-direction: column; gap: 10px; list-style: none; padding: 0; margin: 0; }
.footer-contact-item { display: flex; align-items: flex-start; gap: 10px; font-size: 0.875rem; text-decoration: none; color: inherit; }
.footer-contact-link { transition: color 0.2s, transform 0.2s; }
.footer-contact-link:hover { color: var(--primary-color); transform: translateX(3px); }
.footer-contact-icon { width: 28px; height: 28px; flex-shrink: 0; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; background: color-mix(in srgb, var(--primary-color) 12%, transparent); color: var(--primary-color); }
.footer-col { display: flex; flex-direction: column; }
.footer-col--right { align-items: flex-start; }
@media (min-width: 1024px) { .footer-col--right { align-items: flex-end; } }
.footer-col-heading { font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 20px; color: var(--primary-color); }
.footer-links-list { display: flex; flex-direction: column; gap: 8px; list-style: none; padding: 0; margin: 0; }

/* External links (a tags) */
.footer-link { display: inline-flex; align-items: center; gap: 8px; font-size: 0.875rem; text-decoration: none; color: inherit; opacity: 0.75; transition: opacity 0.2s, gap 0.2s, color 0.2s; }
.footer-link:hover { opacity: 1; color: var(--primary-color); gap: 12px; }

/* Router links in footer (con active state) */
.footer-router-link { display: inline-flex; align-items: center; gap: 8px; font-size: 0.875rem; text-decoration: none; color: inherit; opacity: 0.75; transition: opacity 0.2s, gap 0.2s, color 0.2s; }
.footer-router-link:hover { opacity: 1; color: var(--primary-color); gap: 12px; }

/* Active footer router link */
.footer-router-link.footer-router-link--active {
  opacity: 1;
  color: var(--primary-color);
  font-weight: 700;
}
.footer-router-link.footer-router-link--active .footer-link-arrow {
  transform: translateX(3px);
}

.footer-link-arrow { width: 14px; height: 14px; color: var(--primary-color); transition: transform 0.2s; }
.footer-link:hover .footer-link-arrow,
.footer-router-link:hover .footer-link-arrow { transform: translateX(3px); }

.footer-socials { display: flex; gap: 8px; margin-bottom: 24px; flex-wrap: wrap; }
.footer-social-btn { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1rem; text-decoration: none; color: var(--primary-color); border: 1.5px solid; transition: background 0.2s, color 0.2s, transform 0.2s, border-color 0.2s; }
.footer--light .footer-social-btn { border-color: #e2e8f0; }
.footer--dark  .footer-social-btn { border-color: #1e2a3a; }
.footer-social-btn:hover { background: var(--primary-color); color: white; border-color: var(--primary-color); transform: translateY(-3px); }
.footer-divider { width: 100%; height: 1px; margin-bottom: 24px; }
.footer--light .footer-divider { background: #e8eaf0; }
.footer--dark  .footer-divider { background: #1e2a3a; }
.footer-admin-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; font-size: 0.82rem; font-weight: 700; text-decoration: none; color: white; background: var(--primary-color); box-shadow: 0 4px 14px -4px color-mix(in srgb, var(--primary-color) 60%, transparent); transition: transform 0.2s, box-shadow 0.2s; }
.footer-admin-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -4px color-mix(in srgb, var(--primary-color) 70%, transparent); }
.footer-bottom { display: flex; align-items: center; justify-content: space-between; padding-top: 24px; flex-wrap: wrap; gap: 8px; }
.footer-copy { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; opacity: 0.4; }
.footer-credit { font-size: 0.75rem; opacity: 0.4; font-weight: 500; }
.footer-credit-link { font-weight: 700; text-decoration: none; color: var(--primary-color); opacity: 1; transition: opacity 0.2s; }
.footer-credit-link:hover { opacity: 0.7; }

/* ─── COOKIE BANNER ──────────────────────────────────────── */
.cookie-banner { position: fixed; bottom: 24px; left: 24px; z-index: 100; border-radius: 16px; border: 1px solid; backdrop-filter: blur(16px); box-shadow: 0 8px 32px rgba(0,0,0,0.12); max-width: 420px; overflow: hidden; }
.cookie-banner--light { background: rgba(255,255,255,0.92); border-color: #e2e8f0; color: #1e293b; }
.cookie-banner--dark  { background: rgba(15,23,42,0.92); border-color: #1e2a3a; color: #e2e8f0; }
.cookie-banner::before { content: ''; display: block; height: 3px; background: linear-gradient(90deg, var(--primary-color), var(--accent-color)); }
.cookie-inner { display: flex; align-items: center; gap: 14px; padding: 16px 20px; }
.cookie-icon-wrap { width: 40px; height: 40px; flex-shrink: 0; border-radius: 10px; display: flex; align-items: center; justify-content: center; background: color-mix(in srgb, var(--accent-color) 12%, transparent); color: var(--accent-color); }
.cookie-text { flex: 1; min-width: 0; }
.cookie-title { font-size: 0.82rem; font-weight: 700; margin-bottom: 2px; letter-spacing: 0.01em; }
.cookie-desc { font-size: 0.75rem; opacity: 0.65; line-height: 1.5; }
.cookie-policy-link { color: var(--primary-color); font-weight: 600; text-decoration: none; white-space: nowrap; margin-left: 4px; }
.cookie-policy-link:hover { text-decoration: underline; }
.cookie-accept-btn { flex-shrink: 0; padding: 9px 18px; border-radius: 10px; background: var(--primary-color); color: white; font-size: 0.82rem; font-weight: 700; border: none; cursor: pointer; box-shadow: 0 4px 12px -4px color-mix(in srgb, var(--primary-color) 60%, transparent); transition: transform 0.15s, box-shadow 0.15s; white-space: nowrap; }
.cookie-accept-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px -4px color-mix(in srgb, var(--primary-color) 70%, transparent); }
.cookie-accept-btn:active { transform: scale(0.96); }

/* ─── TRANSITIONS ────────────────────────────────────────── */
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.5s cubic-bezier(0.16,1,0.3,1); }
.slide-up-enter-from { opacity: 0; transform: translateY(40px) scale(0.97); }
.slide-up-leave-to   { opacity: 0; transform: translateX(120%) scale(0.97); }
</style>